// Sprint chatgpt-message-rail (D3 + D5 + D9) — right-edge index of the
// founder's own messages, ChatGPT-style. One bar per founder message (up
// to `RAIL_MAX_BARS`, then bars bucket); hover/focus opens a list of
// previews; clicking one jumps the conversation to it.
//
// D9 — positioning. Two containers, one owner (this file): the OUTER div
// is the overlay (`absolute right-6 inset-y-0 ... pointer-events-none`,
// desktop-only); the bar column is the trigger itself
// (`pointer-events-auto`). No `transform` on the outer div — that would
// create a containing block for Radix's `position: fixed` popper.
//
// D9 — the shared shadcn hover-card wrapper is NOT used: it never portals
// its content (no `HoverCardPrimitive.Portal`), and DS.6 retired it with
// the composer's AI Elements chrome, so nothing shares it after that
// sprint. This component imports the Radix primitive directly and wraps
// its own `Content` in a local `Portal`, taking the floating list out of
// the rail's own `z-10` stacking context.
//
// Builder decision (documented per §8 — within the spec's margin): the
// `data-testid="message-rail"` used by hover targeting (jsdom tests and
// the Playwright spec, D12 ii/vi) sits on the TRIGGER button, not the
// outer wrapper — the outer wrapper is `pointer-events-none`, so an
// element there would fail Playwright's "receives pointer events"
// actionability check.
//
// EC-21 (keyboard selection) is out of v1: Radix's `HoverCardContentImpl`
// sets `tabindex="-1"` on every tabbable node inside the content on every
// render, so Tab cannot reach the list items. No workaround attempted
// here — see `docs/edge-cases.md` EC-21.

import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import { useMemo, useState } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from '../../../i18n';
import type { Message } from '../../../types';
import {
  RAIL_MAX_BARS,
  RAIL_MIN_MESSAGES,
  bucketOf,
  founderMessagesOf,
  previewOf,
} from '../../../lib/message-rail';
import { jumpToMessage } from '../../../lib/jump-to-message';
import { useMessageRail } from '../../../hooks/useMessageRail';

export interface MessageRailProps {
  messages: Message[];
  scrollContainerRef: RefObject<HTMLDivElement>;
}

export function MessageRail({ messages, scrollContainerRef }: MessageRailProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const founderMessages = useMemo(() => founderMessagesOf(messages), [messages]);
  const founderIds = useMemo(() => founderMessages.map((m) => m.id), [founderMessages]);

  const currentId = useMessageRail({ scrollContainerRef, founderIds });

  // D2 — below the threshold, zero DOM.
  if (founderMessages.length < RAIL_MIN_MESSAGES) return null;

  const n = founderMessages.length;
  const barCount = Math.min(n, RAIL_MAX_BARS);
  const currentIndex = currentId ? founderIds.indexOf(currentId) : -1;
  const currentBucket = currentIndex >= 0 ? bucketOf(currentIndex, n, RAIL_MAX_BARS) : -1;

  function handleSelect(id: string) {
    jumpToMessage(scrollContainerRef.current, id);
    setOpen(false);
  }

  return (
    <div
      className="absolute right-6 inset-y-0 z-10 hidden md:flex items-center pointer-events-none"
      aria-hidden="false"
    >
      <HoverCardPrimitive.Root open={open} onOpenChange={setOpen}>
        <HoverCardPrimitive.Trigger asChild>
          {/* QA fix round 1 — `min-w-0` opts this trigger OUT of the global
              touch-target floor (`index.css:141-144`, `:where(button, …)
              { min-width: 44px }`, deliberately low-specificity so
              components can opt out). Without it the trigger widens from
              the 16px D9's gutter arithmetic assumes to 44px, and with the
              right panel open the rail band overlaps the message text by
              ~20px (measured by QA on `Mockups/Dashboard`). `min-h` is left
              alone — the bar column is tall enough on its own. */}
          <button
            type="button"
            data-testid="message-rail"
            aria-label={t('messageRail.label')}
            className="group flex flex-col items-end gap-1.5 pointer-events-auto min-w-0"
          >
            {Array.from({ length: barCount }, (_, i) => (
              <span
                key={i}
                aria-hidden="true"
                className={`h-0.5 w-4 rounded-full transition-colors duration-motion-instant group-hover:bg-muted-foreground ${
                  i === currentBucket ? 'bg-foreground' : 'bg-border'
                }`}
              />
            ))}
          </button>
        </HoverCardPrimitive.Trigger>
        <HoverCardPrimitive.Portal>
          <HoverCardPrimitive.Content
            side="left"
            align="center"
            className="z-50 w-64 rounded-md border bg-popover p-2 text-popover-foreground shadow-md outline-none"
          >
            <ul
              role="list"
              aria-label={t('messageRail.listTitle')}
              className="max-h-80 overflow-y-auto"
            >
              {founderMessages.map((m) => {
                const isCurrent = m.id === currentId;
                const preview = previewOf(m) ?? t('messageRail.attachmentOnly');
                return (
                  <li key={m.id}>
                    {/* QA fix round 2 — same class as the trigger's min-w-0
                        (round 1): `min-h-0` opts this row OUT of
                        index.css:141-144's global 44px touch-target floor.
                        D5 fixes the row at px-2/py-1 (~28px); without the
                        opt-out the floor silently overrides that to 44px
                        per row, and 9 items already overflow max-h-80.
                        Mouse-only target (EC-21 — the list is never
                        keyboard-reachable), so the touch-target floor's
                        own purpose does not apply here. */}
                    <button
                      type="button"
                      onClick={() => handleSelect(m.id)}
                      aria-current={isCurrent ? 'true' : undefined}
                      title={isCurrent ? t('messageRail.current') : undefined}
                      className={`w-full truncate text-left text-body px-2 py-1 rounded-md min-h-0 hover:bg-accent hover:text-accent-foreground ${
                        isCurrent ? 'bg-accent text-accent-foreground' : ''
                      }`}
                    >
                      {preview}
                    </button>
                  </li>
                );
              })}
            </ul>
          </HoverCardPrimitive.Content>
        </HoverCardPrimitive.Portal>
      </HoverCardPrimitive.Root>
    </div>
  );
}
